/**
 * Compare modal — side-by-side view of two solutions.
 *
 * The user opens this from SolutionDetails to compare the current
 * solution against another one from the catalog.
 */
import { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';

import type { Solution, SolutionsCatalog } from '@reefapp/shared';

interface CompareViewProps {
  open: boolean;
  primary: Solution;
  solutions: SolutionsCatalog;
  onClose(): void;
}

function sanitise(template: string): string {
  return DOMPurify.sanitize(template);
}

function SolutionColumn({ solution }: { solution: Solution }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 space-y-3">
      <h3 className="text-lg font-bold text-cyan-300">{solution.name}</h3>
      <div>
        <h4 className="text-sm font-semibold text-slate-400">What it is</h4>
        <p className="text-sm text-slate-300">{solution.what_it_is}</p>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-slate-400">What it does</h4>
        <p className="text-sm text-slate-300">{solution.what_it_does}</p>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-slate-400">Dosing Effect</h4>
        <p className="text-sm font-mono text-cyan-300">{solution.dosing_effect}</p>
      </div>
      <div>
        <h4 className="text-sm font-semibold text-slate-400">Recipe</h4>
        <div
          className="text-sm text-slate-300 mt-1"
          dangerouslySetInnerHTML={{ __html: sanitise(solution.recipe.template) }}
        />
      </div>
    </div>
  );
}

function PlaceholderColumn() {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex items-center justify-center min-h-[200px]">
      <p className="text-slate-500 text-sm">Pick a solution to compare</p>
    </div>
  );
}

export function CompareView({
  open,
  primary,
  solutions,
  onClose,
}: CompareViewProps) {
  const [compareKey, setCompareKey] = useState('');

  const secondary: Solution | null =
    compareKey && solutions[compareKey] ? solutions[compareKey] : null;

  const handleEsc = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEsc);
      return () => document.removeEventListener('keydown', handleEsc);
    }
  }, [open, handleEsc]);

  if (!open) return null;

  const catalogEntries = Object.entries(solutions) as [string, Solution][];
  // Group by category for the secondary picker
  const groups: Record<string, { key: string; name: string }[]> = {};
  for (const [key, sol] of catalogEntries) {
    const cat = sol.category || 'Uncategorized';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push({ key, name: sol.name });
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto m-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-cyan-300">Compare Solutions</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-200 text-2xl leading-none p-1"
          >
            ×
          </button>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Compare against:
          </label>
          <select
            value={compareKey}
            onChange={(e) => setCompareKey(e.target.value)}
            className="w-full sm:w-1/2 bg-slate-700 border border-slate-600 rounded-lg py-2 px-3 text-white focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition"
          >
            <option value="">-- Pick a solution --</option>
            {Object.entries(groups).map(([category, list]) => (
              <optgroup label={category} key={category}>
                {list
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SolutionColumn solution={primary} />
          {secondary ? (
            <SolutionColumn solution={secondary} />
          ) : (
            <PlaceholderColumn />
          )}
        </div>
      </div>
    </div>
  );
}
